import React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import AuthShell from "../components/auth/AuthShell";
import PrimaryButton from "../components/layout/PrimaryButton";
import { AUTH_INTENT_DETAILS, buildLaunchPath, buildLaunchSearch, getLaunchContext } from "../utils/auth/intent";
import { useAuthPageMeta } from "../utils/ui/useAuthPageMeta";

const ENTRY_OPTIONS = [
    { key: "application", intent: "application" },
    {
        key: "xportal",
        intent: "broker",
        source: "xportal",
        returnTo: "/dashboard",
        details: {
            label: "xPortal",
            description: "Access the xPortal workspace.",
        },
    },
    {
        key: "broker-portal",
        intent: "broker",
        source: "broker-portal",
        returnTo: "/broker",
        details: AUTH_INTENT_DETAILS.broker,
    },
    { key: "investor", intent: "investor" },
    { key: "staff", intent: "staff" },
    { key: "client", intent: "client" },
    { key: "referrer", intent: "referrer" },
];

export default function SharedEntry() {
    const location = useLocation();
    const navigate = useNavigate();
    const launchContext = getLaunchContext(location.search);

    useAuthPageMeta("Choose Workspace");

    if (launchContext.hasExplicitIntent && launchContext.isValidIntent) {
        return <Navigate to={buildLaunchPath("/login", launchContext)} replace />;
    }

    const hasInvalidIntent = launchContext.hasExplicitIntent && !launchContext.isValidIntent;

    return (
        <AuthShell
            eyebrow="Financial Edge secure access"
            title="Choose your workspace"
            description={hasInvalidIntent
                ? "That sign-in link could not be matched to a supported destination. Choose the correct workspace below to continue."
                : "Use one secure sign-in to continue to the correct Financial Edge application or portal."}
            align="center"
            footer={<p className="text-sm text-secondary text-center">Only approved destinations are offered through this shared access page.</p>}
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                {ENTRY_OPTIONS.map((option) => {
                    const intent = option.intent;
                    const details = AUTH_INTENT_DETAILS[intent];
                    const displayDetails = option.details || details;
                    const isFuture = details.availability === "future";

                    return (
                        <button
                            key={option.key}
                            type="button"
                            onClick={() => {
                                const search = buildLaunchSearch({
                                    ...launchContext,
                                    intent: option.intent,
                                    source: option.source || launchContext.source,
                                    returnTo: option.returnTo || launchContext.returnTo,
                                });

                                if (isFuture) {
                                    const params = new URLSearchParams(search);
                                    params.set("reason", "not_yet_available");
                                    navigate(`/auth-status?${params.toString()}`);
                                    return;
                                }

                                navigate(`/login${search}`);
                            }}
                            className="rounded-3xl border border-lightBorder bg-white px-6 py-5 text-left transition-all duration-200 hover:border-primary hover:bg-surface focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                        >
                            <div className="flex items-center justify-between gap-4">
                                <span className="text-xl font-bold text-primary">{displayDetails.label}</span>
                                {isFuture ? (
                                    <span className="rounded-full bg-surface px-3 py-1 text-xs font-semibold text-secondary">
                                        Coming soon
                                    </span>
                                ) : null}
                            </div>
                            <p className="mt-3 text-sm text-secondary leading-relaxed">{displayDetails.description}</p>
                        </button>
                    );
                })}
            </div>

            <div className="mt-8">
                <PrimaryButton onClick={() => navigate(buildLaunchPath("/login", launchContext))}>
                    Continue to application sign-in
                </PrimaryButton>
            </div>
        </AuthShell>
    );
}
